import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { transformKeysToSnakeCase, transformKeysToCamelCase } from '../utils/caseTransformer'

/**
 * Список эндпоинтов, для которых НЕ нужно применять трансформацию
 * (например, если они уже используют snake_case или требуют особый формат)
 */
const EXCLUDED_PATHS = [
  // Auth endpoints могут использовать свой формат
  'auth/',
  // Notifications settings endpoint использует snake_case напрямую
  '/notifications/settings',
  // Product endpoints используют snake_case напрямую
  'product/',
  // Goods endpoints (popular, etc.) — ProductCustomCardItem uses snake_case
  'goods/',
  // Favorites products endpoint — uses same ProductCustomCardItem format
  'favorites/products',
  // Recently viewed — same ProductCustomCardItem cards, wrapped in
  // { product_list }. Two words, so the transformer renamed it to productList
  // and the shelf read undefined: the carousel fell back to its demo cards and
  // showed four identical placeholder products on every PDP. May-like was
  // unaffected only because `recommendations` is one word.
  'recently_viewed',
  // v1 normalised the path to the hyphen; the payload is the same snake_case
  // teaser shelf, so it needs the same exemption — the underscore spelling
  // above does not match it.
  'recently-viewed',
  // Shopping-list endpoints используют snake_case напрямую
  'shopping-list',
  // Бекенд документирует тот же ресурс через underscore — тоже не трансформируем
  'shopping_list',
  // Image upload returns { img_url } in snake_case (same as the legacy
  // shopping-list/upload_img); consumers read result.img_url directly.
  'images/upload_img',
  // Image parts answer `{ product_image_parts_data: [...] }` in snake_case and
  // the legacy crop flow (`TempImageSelection.vue`) reads that key verbatim;
  // only `upload_img` was exempt, so the parts arrived as
  // `productImagePartsData` and the crop read undefined. Same family as above.
  'images/get-image-parts',
  // Sizing v2: chart rows are keyed by COLUMN CODES (waist_cm, gucci_it) —
  // the transformer would mangle those dynamic data keys. Keep snake_case,
  // like product/{id}/sizing.
  '/size-charts',
  // SERP-V1: типы фронта СГЕНЕРИРОВАНЫ из OpenAPI бэкенда и потому snake_case
  // (context_echo, next_action, relevance_filtered, image_preview_url).
  // Трансформация переименовала бы их, и компонент читал бы undefined — ровно
  // случай recently_viewed выше, где полка молча показывала демо-карточки.
  'search/you-may-like',
  'personalization/status',
  // PDP-OFFERS (2026-09-06, found on the live stg run): the store comparison
  // lives under `products/{group_id}/offers|boutiques|boutique-cities` — the
  // PLURAL, which `product/` above does not match. Its types are generated
  // from OpenAPI (price_converted, try_on, return_days, cheapest_offer_id,
  // policy_scope, local_price): camelised, the drawer read undefined on every
  // one of them and drew "—", an empty price and no cheapest badge while the
  // API answered every fact.
  'products/',
  // Search history (2026-09-10): `{ recent_searches: [{ id, query: { text,
  // image } }] }` — the FE type `SearchHistoryResponce` reads
  // `recent_searches`, the transformer served `recentSearches`, and both the
  // legacy SearchHistory block and the home drawer's Recent read undefined
  // while the API answered with rows. Same failure as recently_viewed above.
  'search-history',
  // CATEGORY-TREE (2026-09-14): `GET /api/v1/categories/tree` is typed from
  // the backend's OpenAPI (node_id, min_count, search.category_node). Camelised,
  // the drawer read `search.category_node` as undefined and pushed
  // `/searchList?audience=woman` without the node — measured on the stand.
  'categories/tree',
  // FIND SIMILAR (2026-09-14, found on the stand run of the «See all results»
  // page): `GET /api/v1/similar/by-object` and `/similar/by-object/results`
  // are typed from OpenAPI (aspects_available, page_info.page_num, query_id,
  // price_range, items[].price_from). Camelised, the aspect chips never
  // rendered (the drawer and the page read `aspects_available` as undefined)
  // and the page could not see its second page nor its query id.
  'similar/',
  // IMAGE SEARCH (2026-10-12): `POST /api/v1/search/images` is typed from OpenAPI
  // (image_id, preview_url, parent_image_id) — camelised, the drawer would read
  // `image_id` as undefined and search by the whole photo it never uploaded.
  'search/images',
]

/**
 * Пути, сравниваемые ТОЧНО, а не подстрокой.
 *
 * `shouldTransform` матчит подстрокой, и для коротких имён это опасно: `search`
 * как подстрока задел бы чужие `search-history`, `search_history` и
 * `search_results`, сменив поведение v0. Список выше остаётся подстрочным ради
 * совместимости, а короткие имена живут здесь.
 */
const EXCLUDED_EXACT_PATHS = ['search']

/**
 * Проверяет, нужно ли применять трансформацию для данного URL
 */
/**
 * Пути, сравниваемые ПО ПЕРВОМУ СЕГМЕНТУ. `brands` подстрокой задел бы v0
 * (`brand/brands`, `user/style/brands`, `favorite-brands`, `size-recommendation/brands`),
 * чьи потребители читают camelCase; а `/api/v1/brands…` (BRANDS, 2026-09-14) типизирован
 * из OpenAPI в snake_case (`letter_group`, `products_count`, `cover_url`, `is_followed`,
 * `similar_brands`, `catalog_version`) и без исключения чипы и счётчики читали бы undefined.
 */
const EXCLUDED_PREFIXES = ['brands']

function shouldTransform(url: string | undefined): boolean {
  if (!url) return false
  const normalized = url.replace(/^\/+/, '').replace(/\?.*$/, '')
  if (EXCLUDED_EXACT_PATHS.includes(normalized)) return false
  const first = normalized.split('/')[0]
  if (first && EXCLUDED_PREFIXES.includes(first)) return false
  return !EXCLUDED_PATHS.some((path) => url.includes(path))
}

/**
 * Прикрепляет interceptors для автоматической трансформации
 * camelCase ↔ snake_case между фронтендом и бекендом
 */
export function attachCaseTransformerInterceptors(client: AxiosInstance): void {
  // Request interceptor: преобразуем camelCase → snake_case
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Не применяем трансформацию к query параметрам - они могут быть уже в нужном формате
      // Трансформируем только body данных
      if (config.data && shouldTransform(config.url)) {
        config.data = transformKeysToSnakeCase(config.data)
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor: преобразуем snake_case → camelCase
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.data && shouldTransform(response.config.url)) {
        response.data = transformKeysToCamelCase(response.data)
      }
      return response
    },
    (error) => {
      // Также трансформируем данные ошибок, если они есть
      if (error.response?.data && shouldTransform(error.config?.url)) {
        error.response.data = transformKeysToCamelCase(error.response.data)
      }
      return Promise.reject(error)
    }
  )
}


